import { useState } from "react";

import { getErrorMessage } from "@luce/core/apollo";
import { pickMedia } from "@luce/core/platform";
import type { PickedMedia } from "@luce/core/platform";
import {
  AdaptiveModal,
  Box,
  Button,
  Icon,
  IconButton,
  Image,
  Pressable,
  toast,
  Typography,
} from "@luce/ui";

import { usePaymentProof } from "../../gqlhooks/use-payment-proof";

interface PaymentProofSheetProps {
  invoiceId: string;
  /** Proof already on the invoice. */
  uploadedUrl: string | null;
  open: boolean;
  onOpenChange(open: boolean): void;
}

const PREVIEW_SIZE = 96;

export function PaymentProofSheet({
  invoiceId,
  uploadedUrl,
  open,
  onOpenChange,
}: PaymentProofSheetProps) {
  const { addPaymentProof, removePaymentProof, loading } = usePaymentProof(invoiceId);
  const [picked, setPicked] = useState<PickedMedia | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) setPicked(null);
    onOpenChange(next);
  };

  const previewUrl = picked?.uri ?? uploadedUrl;

  const onPick = async () => {
    const media = await pickMedia({ type: "images" });
    if (media) setPicked(media);
  };

  const onRemove = async () => {
    if (picked) {
      setPicked(null);
      return;
    }
    try {
      await removePaymentProof();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove payment proof"));
    }
  };

  const onSubmit = async () => {
    if (!picked) return;
    try {
      await addPaymentProof(picked.file);
      toast.success("Payment proof uploaded");
      handleOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to upload payment proof"));
    }
  };

  return (
    <AdaptiveModal.Root open={open} onOpenChange={handleOpenChange} dialogSize="sm">
      <AdaptiveModal.Header>
        <AdaptiveModal.Title>Attachments</AdaptiveModal.Title>
        <AdaptiveModal.Description>Upload your payment proof</AdaptiveModal.Description>
      </AdaptiveModal.Header>

      <AdaptiveModal.Body>
        <Box className="py-spacing-sm flex flex-row">
          {previewUrl ? (
            <Box className="relative">
              <Box className="rounded-radius-lg overflow-hidden">
                <Image
                  src={previewUrl}
                  alt="Payment proof"
                  width={PREVIEW_SIZE}
                  height={PREVIEW_SIZE}
                  contentFit="cover"
                />
              </Box>
              <Box className="absolute -right-2 -top-2">
                <IconButton
                  icon="XCircle"
                  variant="destructive-secondary"
                  size="sm"
                  disabled={loading}
                  onClick={onRemove}
                  accessibilityLabel="Remove payment proof"
                />
              </Box>
            </Box>
          ) : (
            <Pressable
              onClick={onPick}
              accessibilityRole="button"
              accessibilityLabel="Add photo"
              className="gap-spacing-xs flex flex-col items-center"
            >
              <Box className="rounded-radius-lg bg-luce-brand-primarySurface flex h-24 w-24 flex-row items-center justify-center">
                <Icon
                  name="CameraPlus"
                  size={24}
                  weight="fill"
                  className="text-luce-brand-primary"
                />
              </Box>
              <Typography variant="caption-md" color="neutral-foreground-default">
                Add Photo
              </Typography>
            </Pressable>
          )}
        </Box>
      </AdaptiveModal.Body>

      <AdaptiveModal.Footer>
        <Button
          variant="primary"
          size="lg"
          label="Submit"
          fullWidth
          loading={loading}
          disabled={!picked || loading}
          onClick={onSubmit}
        />
      </AdaptiveModal.Footer>
    </AdaptiveModal.Root>
  );
}
